import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Shirt, ChevronDown, ChevronUp, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import DressCodeGrid from '@/components/dress-code/DressCodeGrid';
import TemplateSuggestions from '@/components/dress-code/TemplateSuggestions';
import { dressCodeService } from '@/lib/api/dressCode.service';
import { dressCodeTemplateService } from '@/lib/api/dressCodeTemplate.service';
import { eventService } from '@/lib/api/event.service';
import {
  dressCodeGridKey,
  GENDERS,
  AGE_CATEGORIES,
} from '@/types/dressCode.types';
import type {
  EventDressCode,
  DressCodeGrid as DressCodeGridType,
  DressCodeTemplateGroup,
  Gender,
  AgeCategory,
  UpdateDressCodeDto,
} from '@/types/dressCode.types';

const EventDressCodePage: React.FC = () => {
  const { celebrationId, eventId } = useParams<{ celebrationId: string; eventId: string }>();
  const { t } = useTranslation(['dresscode']);


  const [eventTitle, setEventTitle] = useState('');
  const [dressCodeEnabled, setDressCodeEnabled] = useState(false);
  const [grid, setGrid] = useState<DressCodeGridType>({});
  const [suggestions, setSuggestions] = useState<DressCodeTemplateGroup[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const cId = parseInt(celebrationId ?? '0');
  const eId = parseInt(eventId ?? '0');

  // ─── Load initial data ───────────────────────────────────────────────────

  useEffect(() => {
    if (!eId || !cId) return;

    (async () => {
      try {
        const [event, dressCodes] = await Promise.all([
          eventService.getEvent(eId),
          dressCodeService.getDressCodes(eId, cId),
        ]);

        setEventTitle(event.title ?? 'Event');
        setDressCodeEnabled(event.dress_code_enabled ?? false);
        setGrid(buildGrid(dressCodes));
      } catch (err) {
        setError(t('dresscode:page.errors.loadFailed'));
        console.error(err);
      }
    })();
  }, [eId, cId]);

  // ─── Load suggestions when panel opened ─────────────────────────────────

  useEffect(() => {
    if (!showSuggestions || suggestions.length > 0) return;
    setLoadingSuggestions(true);
    dressCodeTemplateService
      .getSuggestions(cId)
      .then(setSuggestions)
      .catch(err => console.error('Failed to load suggestions:', err))
      .finally(() => setLoadingSuggestions(false));
  }, [showSuggestions, cId]);

  // ─── Helpers ─────────────────────────────────────────────────────────────

  function buildGrid(entries: EventDressCode[]): DressCodeGridType {
    const g: DressCodeGridType = {};
    for (const e of entries) {
      g[dressCodeGridKey(e.gender, e.age_category)] = e;
    }
    return g;
  }

  // ─── Toggle dress code on/off ─────────────────────────────────────────────

  async function handleToggle(enabled: boolean) {
    setDressCodeEnabled(enabled);
    try {
      await dressCodeService.toggleDressCode(eId, cId, enabled);
    } catch (err) {
      console.error('Failed to toggle dress code:', err);
      setDressCodeEnabled(!enabled); // revert
    }
  }

  // ─── Cell change (updates local grid, marks dirty) ──────────────────────

  const handleCellChange = useCallback(
    (
      gender: Gender,
      ageCategory: AgeCategory,
      dto: UpdateDressCodeDto & { description: string }
    ) => {
      const key = dressCodeGridKey(gender, ageCategory);
      setGrid(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          gender,
          age_category: ageCategory,
          description: dto.description,
          avoid: dto.avoid ?? null,
          celebration_id: cId,
        } as EventDressCode,
      }));
      setDirty(true);
    },
    [cId]
  );

  // ─── Save all cells ──────────────────────────────────────────────────────

  async function handleSaveAll() {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const entries = Object.values(grid).filter(
        (e): e is EventDressCode => !!e && !!(e.description || e.avoid)
      );

      const saved = await Promise.all(
        entries.map(async entry => {
          const dto = {
            celebration_id: cId,
            description: entry.description,
            avoid: entry.avoid ?? undefined,
          };
          if (entry.id) {
            return dressCodeService.updateDressCode(eId, entry.id, dto);
          }
          return dressCodeService.createDressCode(eId, {
            ...dto,
            gender: entry.gender,
            age_category: entry.age_category,
          });
        })
      );

      const newGrid: DressCodeGridType = { ...grid };
      for (const s of saved) {
        newGrid[dressCodeGridKey(s.gender, s.age_category)] = s;
      }
      setGrid(newGrid);
      setDirty(false);
      setSuccessMessage(t('dresscode:page.success.saved'));
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      console.error('Failed to save dress code:', err);
        setError(t('dresscode:page.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  // ─── Copy one cell to all ─────────────────────────────────────────────────

  const handleCopyToAll = useCallback(
    async (gender: Gender, ageCategory: AgeCategory) => {
      const source = grid[dressCodeGridKey(gender, ageCategory)];
      if (!source) return;

      setSaving(true);
      try {
        const updates = await Promise.all(
          GENDERS.flatMap(g =>
            AGE_CATEGORIES.map(async age => {
              if (g === gender && age === ageCategory) return null;
              const result = await dressCodeService.createDressCode(eId, {
                celebration_id: cId,
                gender: g,
                age_category: age,
                description: source.description,
                avoid: source.avoid ?? undefined,
              });
              return result;
            })
          )
        );

        const newGrid: DressCodeGridType = { ...grid };
        for (const result of updates) {
          if (result) newGrid[dressCodeGridKey(result.gender, result.age_category)] = result;
        }
        setGrid(newGrid);
      } catch (err) {
        console.error('Failed to copy to all:', err);
      } finally {
        setSaving(false);
      }
    },
    [grid, eId, cId]
  );

  // ─── Apply template ───────────────────────────────────────────────────────

  async function handleApplyTemplate(templateName: string) {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const result = await dressCodeService.bulkCreate(eId, {
        celebration_id: cId,
        template_name: templateName,
      });
      
      // Reload full grid so pre-existing cells not covered by the template are preserved
      const all = await dressCodeService.getDressCodes(eId, cId);
      
      setGrid(buildGrid(all));
      setShowSuggestions(false);
      setSuccessMessage(t('dresscode:page.success.applied', { templateName, count: result.length }));
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Failed to apply template:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to apply template';
      setError(t('dresscode:page.errors.applyFailed', { message: errorMessage }));
    } finally {
      setSaving(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs />
      {/* Header */}
      <div className="flex items-center gap-2">
        <Shirt className="w-5 h-5 text-indigo-500" />
        <div>
          <h1 className="text-xl font-semibold">{t('dresscode:page.title')}</h1>
          {eventTitle && (
            <p className="text-sm text-muted-foreground">{eventTitle}</p>
          )}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3 border border-red-200">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="text-sm text-green-600 bg-green-50 rounded-lg p-3 border border-green-200">
          {successMessage}
        </div>
      )}

      {/* Enable toggle card */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center gap-3">
            <Switch
              id="dress-code-toggle"
              checked={dressCodeEnabled}
              onCheckedChange={handleToggle}
            />
            <Label htmlFor="dress-code-toggle" className="text-sm font-medium cursor-pointer">
              {t('dresscode:page.toggle.label')}
            </Label>
            <span className="text-xs text-gray-400 ml-1">
              {t('dresscode:page.toggle.hint')}
            </span>
          </div>
        </CardContent>
      </Card>

      {dressCodeEnabled && (
        <>
          {/* Template suggestions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{t('dresscode:page.templateSuggestions.title')}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-sm"
                  onClick={() => setShowSuggestions(v => !v)}
                >
                  {showSuggestions ? (
                    <><ChevronUp className="w-4 h-4" /> {t('dresscode:page.templateSuggestions.hide')}</>
                  ) : (
                    <><ChevronDown className="w-4 h-4" /> {t('dresscode:page.templateSuggestions.browse')}</>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                {t('dresscode:page.templateSuggestions.subtitle')}
              </p>
            </CardHeader>
            {showSuggestions && (
              <CardContent>
                <TemplateSuggestions
                  suggestions={suggestions}
                  loading={loadingSuggestions}
                  onApply={handleApplyTemplate}
                />
              </CardContent>
            )}
          </Card>

          <Separator />

          {/* Dress code grid */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('dresscode:page.dressCodeGrid.title')}</CardTitle>
              <p className="text-xs text-gray-500">
                {t('dresscode:page.dressCodeGrid.subtitle')}
              </p>
            </CardHeader>
            <CardContent>
              <DressCodeGrid
                grid={grid}
                celebrationId={cId}
                onCellChange={handleCellChange}
                onCopyToAll={handleCopyToAll}
                disabled={saving}
              />
            </CardContent>
          </Card>

          {/* Save button */}
          <div className="flex items-center justify-end gap-3">
            {dirty && (
              <span className="text-xs text-amber-600">{t('dresscode:page.saveBar.unsavedChanges')}</span>
            )}
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t('dresscode:page.saveBar.saving')}</>
              ) : successMessage ? (
                <><CheckCircle2 className="w-4 h-4" /> {t('dresscode:page.saveBar.saved')}</>
              ) : (
                <><Save className="w-4 h-4" /> {t('dresscode:page.saveBar.save')}</>
              )}
            </Button>
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default EventDressCodePage;
